import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { userPool as userpool } from '../constants/constants';
import Navbar from "../components/Navbar";
import SessionUpload from "../components/SessionUpload";
import SessionStatus from "../components/SessionStatus";
import TalkTreeResources from "../components/Resources";
import SessionStream from "../components/SessionStreaming/StreamSetup";
import TalkTreeLibrary from "../components/TalkTreeLibrary";
import { MENU_IDS, PRIVACY_POLICY_URL, COGNITO_TOKEN_URL } from "../constants/constants";
import { getAuthToken, getRefreshToken, getTokenExpiry, setAuthToken, setTokenExpiry, clearAuthStorage } from '../utils/auth';
import { getLoggedInUser, getLoggedInUserName } from '../utils/auth';
import { getCognitoSignOutUrl } from '../authConfig';
import { markRealTreeCreated } from '../utils/onboarding';
import "../App.css";
import {ThemeProvider} from '@mui/material/styles';
import { appTheme } from '../talkTreeShared/Tree/constants/theme';
import TreeSimulator from "../components/TreeSimulator";

const HASH_TO_MENU = {
  '/library': MENU_IDS.LIBRARY,
  '/resources': MENU_IDS.RESOURCES,
  '/recordings': MENU_IDS.RECORDINGS,
  '/upload': MENU_IDS.UPLOAD,
  '/stream': MENU_IDS.STREAM,
  '/simulator': MENU_IDS.SIMULATOR,
};

const MENU_TO_HASH = Object.fromEntries(
  Object.entries(HASH_TO_MENU).map(([hash, menu]) => [menu, hash])
);
function Dashboard() {
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState(MENU_IDS.LIBRARY);
  const [userName, setUserName] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [toast, setToast] = useState(null);
  const streamRef = useRef(null);
  const [streamSessionActive, setStreamSessionActive] = useState(false);
  const showUploadSuccessToast = () => {
  setToast({
    type: 'success',
    title: 'Your session is now processing.',
    message: 'We will email you when your Talk Tree is ready. See the processing status to see progress. Click Refresh to get the latest status.',
  });
};
  const [uploadFormState, setUploadFormState] = useState({
    title: "",
    description: "",
    file: null,
    duration: null,
    sessionMetadata: null,
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const loggingOutRef = useRef(false);

  useEffect(() => {
  const hash = window.location.hash.slice(1);
  if (HASH_TO_MENU[hash]) {
    setActiveMenu(HASH_TO_MENU[hash]);
  } else if (!hash) {
    window.location.hash = '/library';
  }
  setIsInitialized(true);
}, []);

  useEffect(() => {
  if (!isInitialized) return;
  const targetHash = MENU_TO_HASH[activeMenu];
  if (targetHash) {
    const currentHash = window.location.hash.slice(1);
    if (currentHash !== targetHash) {
      window.location.hash = targetHash;
    }
  }
}, [activeMenu, isInitialized]);

 useEffect(() => {
  const handleHashChange = () => {
    const hash = window.location.hash.slice(1);
    if (HASH_TO_MENU[hash]) {
      setActiveMenu(HASH_TO_MENU[hash]);
    }
  };
  window.addEventListener('hashchange', handleHashChange);
  return () => window.removeEventListener('hashchange', handleHashChange);
}, []);

  useEffect(() => {
    const accessToken = getAuthToken();
    if (!accessToken) { navigate("/"); return; }
    const cognitoUser = userpool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.getSession((err, session) => {
        if (err || !session.isValid()) { navigate("/"); return; }
        cognitoUser.getUserAttributes((err, attributes) => {
          if (!err && attributes) {
            const givenName = attributes.find((a) => a.Name === "given_name");
            if (givenName) setUserName(givenName.Value);
          }
        });
      });
    } else {
      // Under google sso flow, we capture the display name from the id_token returned,
      // if no display name is available in the payload, we fall back to the loggedInUser (email_id)
      const displayName = getLoggedInUserName();
      const loggedInUser = getLoggedInUser();
      if (displayName) {
        setUserName(displayName);
      } else if (loggedInUser) {
        setUserName(loggedInUser);
      }
    }
  }, [navigate]);
  useEffect(() => {
    if (!isInitialized) return;
  }, [activeMenu, isInitialized]);

  const handleLogout = async () => {
    if (loggingOutRef.current) return; 
    loggingOutRef.current = true;
    try {
      await Promise.race([
        streamRef.current?.stopSession?.(),
        new Promise((resolve) => setTimeout(resolve, 4000)), // never block logout
      ]);
    } catch { /* ignore */ }

    mixpanel.track('userLoggedOut');
    mixpanel.reset();
    clearAuthStorage();
    const cognitoUser = userpool.getCurrentUser();
    if (cognitoUser) cognitoUser.signOut();
    // Use origin only to match common Cognito allowed sign-out URL setup.
    const logoutUri = window.location.origin;
    window.location.replace(getCognitoSignOutUrl(userpool.clientId, logoutUri));
  };
  const handleMenuChange = async (nextMenu) => {
    if (streamSessionActive && nextMenu !== MENU_IDS.STREAM) {
      const confirmed = window.confirm(
        'You have an active recording session. Leaving this page will stop it. Continue?'
      );
      if (!confirmed) return;
      mixpanel.track('activeStreamInterrupted', { destinationMenu: nextMenu });
      await streamRef.current?.stopSession();
      setStreamSessionActive(false); 
      setActiveMenu(nextMenu);
      return;
    }
    setActiveMenu(nextMenu);
  };

  const isTokenExpired = async () => {
    const refreshToken = getRefreshToken();
    let expiryTime = parseInt(getTokenExpiry(), 10);
    if (Number.isNaN(expiryTime)) {
      handleLogout();
      return false;
    }
    // Backward compatibility for previously stored second-based expiries.
    if (expiryTime < 1000000000000) {
      expiryTime *= 1000;
    }
    if (Date.now() < expiryTime - 60000) return true;
    if (!refreshToken) {
      handleLogout();
      return false;
    }
    try {
      const response = await fetch(
        COGNITO_TOKEN_URL,
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            grant_type: "refresh_token",
            client_id: userpool.clientId,
            refresh_token: refreshToken,
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Token refresh request failed");
      }
      const data = await response.json();
      if (!data.id_token) {
        throw new Error("Token refresh response missing id_token");
      }
      setAuthToken(data.id_token);
      const expiresInSeconds = Number(data.expires_in) || 3600;
      setTokenExpiry(String(Date.now() + expiresInSeconds * 1000));
      return true;
    } catch (err) {
      console.error("Token refresh error:", err);
      handleLogout();
      return false;
    }
  };

  // Map MENU_IDS to human-readable page titles for aria-live announcements
  const pageTitles = {
    [MENU_IDS.LIBRARY]: "Talk Tree Library",
    [MENU_IDS.RESOURCES]: "Resources",
    [MENU_IDS.RECORDINGS]: "Recordings",
    [MENU_IDS.UPLOAD]: "Upload Recording",
    [MENU_IDS.STREAM]: "Stream",
    [MENU_IDS.SIMULATOR]: "Simulator",
  };

  return (
    <ThemeProvider theme={appTheme}>
    <div style={{ minHeight: "100vh", backgroundColor: "#f9f7f6",  display: "flex", flexDirection: "column" }}>

      <Navbar
        activeMenu={activeMenu}
        setActiveMenu={handleMenuChange}
        userName={userName}
        handleLogout={handleLogout}
      />

      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap" }}
      >
        {pageTitles[activeMenu] || ""}
      </div>

      <main
        id="main-content"
        tabIndex={-1}
        aria-label={pageTitles[activeMenu] || "Main content"}
        style={{
          outline: "none",
          flex: 1,
          paddingTop: "16px",
          paddingBottom: "16px",
          paddingLeft: "28px",
          paddingRight: "28px",
        }}
      >
        {activeMenu === MENU_IDS.LIBRARY && (
          <TalkTreeLibrary
            isTokenExpired={isTokenExpired}
            highlightedSessionId={selectedSessionId}
            setActiveMenu={setActiveMenu}
            onHighlightConsumed={() => setSelectedSessionId(null)}
          />
        )}
        {activeMenu === MENU_IDS.RESOURCES && <TalkTreeResources />}
        {activeMenu === MENU_IDS.RECORDINGS && (
          <SessionStatus
            isTokenExpired={isTokenExpired}
            setActiveMenu={setActiveMenu}
            onSessionSelect={(sessionId) => {
              setSelectedSessionId(sessionId);
              setActiveMenu(MENU_IDS.LIBRARY);
            }}
            toast={toast}
            onToastClose={() => setToast(null)}
          />
        )}
        {activeMenu === MENU_IDS.UPLOAD && (
          <SessionUpload
            isTokenExpired={isTokenExpired}
            onResourcesClick={() => setActiveMenu(MENU_IDS.RESOURCES)}
            setActiveMenu={setActiveMenu}
            uploadFormState={uploadFormState}
            setUploadFormState={setUploadFormState}
            showSuccessToast={showUploadSuccessToast}
          />
        )}
          {activeMenu === MENU_IDS.STREAM && (
          <SessionStream
            ref={streamRef}
            isTokenExpired={isTokenExpired}
            onGoToRecordings={() => setActiveMenu(MENU_IDS.RECORDINGS)}
            showSuccessToast={(t) => setToast(t)}
            onSessionActiveChange={setStreamSessionActive}
          />
        )}
        {activeMenu === MENU_IDS.SIMULATOR && (
          <TreeSimulator
            isTokenExpired={isTokenExpired}
            onGoToRecordings={() => setActiveMenu(MENU_IDS.RECORDINGS)}
            showSuccessToast={(t) => setToast(t)}
            onTreeSaved={(treeId) => {
              markRealTreeCreated();
              setSelectedSessionId(treeId);
              setActiveMenu(MENU_IDS.LIBRARY);
            }}
          />
        )}
      </main>

      {/* Footer */}
      <footer 
      aria-label="Site footer"
      style={{
        textAlign: "center",
        padding: "16px 24px",
        fontSize: "13px",
        color: "#6b7280",
         
        backgroundColor: "#f9f7f6",
      }}>
      
        By using this app, you agree to our{" "}
        <a
          href={PRIVACY_POLICY_URL}
          target="talk-trees-privacy"
          rel="noreferrer"
          aria-label="Privacy Policy (opens in new tab)"
          style={{ color: "#1F72E7", fontWeight: 500, textDecoration: "none" }}
          onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
        >
          Privacy Policy
        </a>
        {" "}and how we keep your information safe.
      </footer>

    </div>
    </ThemeProvider>
  );
}

export default Dashboard;